'use client';

import React, { useState, useCallback } from 'react';
import { ScreenContent, ScreenTabBar, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { EmptyState, Button, ButtonGroup, OverlayBadge } from '../../components/ui';
import { PriceDisplay } from '../../components/PriceDisplay';
import { ItemListItem } from '../../components/ItemListItem';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useShopSkins, type ShopSkinsFilter } from '../../queries/useShopQueries';
import { SkinsFilterModal, DEFAULT_SKINS_FILTERS, type SkinsFilterValues } from './SkinsFilterModal';
import { ShopFiltersPanel, DEFAULT_FILTERS, type FilterValues } from './ShopFiltersPanel';
import { GetShopSkinsSortBy } from '../../services/generated';
import type { ShopSkin } from '../../services/generated/Api';
import type { ScreenParams } from '../../types/navigation';
import translations from './ShopScreen.translations';
import { FaSlidersH } from '../../components/icons';
interface ShopSkinsScreenProps {
  params?: ScreenParams & {
    weapon?: string;
    category?: string;
    weaponImage?: string;
  };
  onBack?: () => void;
}

export const ShopSkinsScreen: React.FC<ShopSkinsScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();

  const weapon = params?.weapon ?? '';
  const category = params?.category ?? '';

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<SkinsFilterValues>(DEFAULT_SKINS_FILTERS);

  // Convert SkinsFilterValues to ShopSkinsFilter for the query
  const queryFilters: ShopSkinsFilter = {
    rarity: filters.rarity || undefined,
    sortBy: filters.sortBy as GetShopSkinsSortBy || undefined,
  };

  const {
    data: skins = [],
    isLoading,
    error,
    refetch,
  } = useShopSkins(weapon, queryFilters);

  const handleApplyFilters = useCallback((newFilters: SkinsFilterValues) => {
    setFilters(newFilters);
    setIsFilterModalOpen(false);
  }, []);

  const handleSkinClick = useCallback(
    (skin: ShopSkin) => {
      navigation.navigate('shop-items', {
        weapon,
        skinName: skin.skinName,
        category,
        title: skin.skinName,
      });
    },
    [navigation, weapon, category]
  );

  const skinsContent = (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center min-h-[200px]"><LoadingSpinner /></div>
      ) : error ? (
        <ErrorMessage error={error instanceof Error ? error.message : t('errorLoadSkins')} onRetry={() => refetch()} />
      ) : skins.length === 0 ? (
        <EmptyState title={t('noSkinsFound')} description="" />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1 pt-2">
          {skins.map((skin) => (
            <ItemListItem
              key={skin.skinName}
              className="flex flex-col cursor-pointer transition-transform duration-150 ease-smooth overflow-hidden active:scale-[0.97]"
              item={{ name: `${weapon} | ${skin.skinName}`, imageUrl: skin.imageUrl, rarityColor: skin.rarityColor }}
              onClick={() => handleSkinClick(skin)}
              hideCondition
            >
              <OverlayBadge>
                <span className="flex items-center gap-1 text-sm font-semibold text-amber-400">
                  {t('from')} <PriceDisplay priceInCredits={skin.minPriceInCredits} />
                </span>
              </OverlayBadge>
            </ItemListItem>
          ))}
        </div>
      )}
    </>
  );

  // Desktop: convert skins filters to ShopFiltersPanel format
  const desktopFilters: FilterValues = {
    ...DEFAULT_FILTERS,
    category: category,
    weapon: weapon,
    rarity: filters.rarity,
    sortBy: filters.sortBy,
  };

  const handleDesktopFilterApply = useCallback((f: FilterValues) => {
    setFilters({ rarity: f.rarity, sortBy: f.sortBy });
  }, []);

  if (isDesktop) {
    return (
      <ScreenContent scrollKey={`shop-skins-${weapon}`}>
        <div className="flex gap-6 p-6">
          <div className="w-[260px] shrink-0 sticky top-6 self-start">
            <div className="rounded-2xl border border-white/[0.06] p-5" style={{ background: 'linear-gradient(180deg, rgba(15,18,30,0.95) 0%, rgba(10,12,20,0.98) 100%)' }}>
              <h2 className="text-sm font-bold text-white m-0 mb-3">{weapon}</h2>
              <ShopFiltersPanel filters={desktopFilters} onApply={handleDesktopFilterApply} onReset={() => {
                navigation.resetStack('games', 'shop' as any);
              }} />
            </div>
          </div>
          <div className="flex-1 min-w-0">{skinsContent}</div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenFixedTop title={weapon} />
      <ScreenTabBar />
      <ScreenFixedBottom>
        <ButtonGroup>
          <Button size="l" before={<FaSlidersH />} onClick={() => setIsFilterModalOpen(true)} stretched>{t('showFilters')}</Button>
        </ButtonGroup>
      </ScreenFixedBottom>
      <ScreenContent scrollKey={`shop-skins-${weapon}`}>
        <div className="pb-[calc(72px+16px+var(--tg-viewport-safe-area-inset-bottom,0px))]">
          {skinsContent}
        </div>
      </ScreenContent>
      <SkinsFilterModal isOpen={isFilterModalOpen} filters={filters} onApply={handleApplyFilters} onClose={() => setIsFilterModalOpen(false)} />
    </>
  );
};
